import React, { useState } from "react";
import { Input, Tooltip, InputGroup, InputGroupText } from "reactstrap";
import { RiEyeLine, RiEyeOffLine } from "react-icons/ri";

const PasswordInput = ({
    label = "Password",
    placeholder = "Enter your password",
    required = false,
    layout = "below",
    value = "",
    name = "password",
    id = "passwordInput",
    onChange = () => { },
    onBlur = () => { },
    requiredErrorMessage = "Password is required.",
    showHelp = false,
    helpText = "Password must be strong and meet the criteria.",
    maxLength = 16, // New prop for maximum length
}) => {
    const [inputValue, setInputValue] = useState(value);
    const [error, setError] = useState(""); // Stores error messages
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    // Regex pattern for validation
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,16}$/;

    // Validation function
    const validatePassword = (value) => {
        if (required && !value) {
            setError(requiredErrorMessage);
            return false;
        }
        if (value.length < 8 || value.length > maxLength) {
            setError(`Password must be between 8 and ${maxLength} characters long.`);
            return false;
        }
        if (!/[A-Z]/.test(value)) {
            setError("Password must contain at least one uppercase letter (A-Z).");
            return false;
        }
        if (!/[a-z]/.test(value)) {
            setError("Password must contain at least one lowercase letter (a-z).");
            return false;
        }
        if (!/\d/.test(value)) {
            setError("Password must contain at least one digit (0-9).");
            return false;
        }
        if (!/[@$!%*?&]/.test(value)) {
            setError("Password must contain at least one special character (!@#$%^&*?).");
            return false;
        }
        if (!passwordRegex.test(value)) {
            setError("Password must meet all complexity requirements.");
            return false;
        }

        setError(""); // Clear error when password is valid
        return true;
    };

    // Handle input change
    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        validatePassword(newValue);
        onChange(e);
    };

    // On blur, validate input
    const handleInputBlur = (e) => {
        validatePassword(e.target.value);
        onBlur(e);
    };

    // Toggle password visibility
    const togglePasswordVisibility = () => {
        setShowPassword((prev) => !prev);
    };

    return (
        <div className={layout === "inline" ? "d-flex align-items-center mb-2" : "mb-2"}>
            <label htmlFor={id} className="d-flex" style={{ width: layout === "inline" ? "25%" : "100%" }}>
                <span>
                    {label}
                    {required && <span className="text-danger ml-1">*</span>}
                    {showHelp && (
                        <span id={`helpTooltip-${id}`} className="ms-1" style={{ position: "relative", top: "-3px" }}>
                            <i className="ri-question-line" style={{ cursor: "pointer", fontSize: "16px" }}></i>
                        </span>
                    )}
                </span>

            </label>
            {showHelp && (
                <Tooltip
                    placement="right"
                    isOpen={tooltipOpen}
                    target={`helpTooltip-${id}`}
                    toggle={() => setTooltipOpen(!tooltipOpen)}
                >
                    {helpText}
                </Tooltip>
            )}
            <div className="w-100">
                <InputGroup>
                    <Input
                        type={showPassword ? "text" : "password"}
                        name={name}
                        id={id}
                        placeholder={placeholder}
                        value={inputValue}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        // maxLength={maxLength} // Apply maxLength to input
                        style={error ? { borderColor: "red" } : {}}
                    />
                    <InputGroupText onClick={togglePasswordVisibility} style={{ cursor: "pointer" }}>
                        {showPassword ? <RiEyeOffLine size={15} /> : <RiEyeLine size={15} />}
                    </InputGroupText>
                </InputGroup>

                {/* Custom error message (only shown when an error exists) */}
                {error && <div style={{ color: "#f06548", fontSize: "0.875em", marginTop: "5px" }}>{error}</div>}
            </div>
        </div>
    );
};

export default PasswordInput;
